import { useAtomValue, useSetAtom } from 'jotai';

import { Card } from '@growtherapy/sprout-ui';

import {
  NudgeObject,
  TreatmentPlanStatus,
  TreatmentPlansOverviewConnected,
  TreatmentPlansOverviewConnectedProps,
} from '@grow-therapy-team/seedling-components';

import { EventTypeEnum } from '../../../segment/types';
import {
  ProviderDrawerState,
  drawerStateAtom,
  isInSessionWithSelectedClientAtom,
  selectedClientInfoAtom,
  selectedPatientId,
  treatmentPlanContentStatus,
} from '../../state';
import { useGetFormsByPatientShortId } from '../../useGetFormsByPatientShortId';
import { ClientInformationFormsAndQuestionnaires } from '../ClientInformationFormsAndQuestionnaires';
import { ClientInformationProgressWrapper as ClientInformationProgress } from '../ClientInformationProgress';
import { ClinicalConsiderationsSectionWrapper as ClinicalConsiderationsSection } from '../ClinicalConsiderationsSection';
import { completedFormsForSelectedClientAtom, hasNewFormsAtomFamily } from '../state';
import { CombinedFormDataEntriesByFormTitle } from '../types';
import { useSendClientInfoTabViewTrackingEvent } from '../useSendClientInfoTabViewTrackingEvent';

interface TreatmentTabProps {
  patientShortId: string;
  completedForms?: CombinedFormDataEntriesByFormTitle;
  hasNewForms?: boolean;
  nudges: NudgeObject[];
  patientId: string;
  shouldShowFormsSection?: boolean;
  isInSessionWithSelectedClient: boolean;
  shouldShowClinicalConsiderationSection: boolean;
  onClickTreatmentPlans: (treatmentPlanStatus: TreatmentPlanStatus) => void;
  treatmentPlansComponent: React.FC<TreatmentPlansOverviewConnectedProps>;
  clientInformationProgress: React.FC;
}

export function TreatmentTab({
  completedForms,
  hasNewForms,
  patientId,
  patientShortId,
  shouldShowFormsSection,
  onClickTreatmentPlans,
  isInSessionWithSelectedClient,
  shouldShowClinicalConsiderationSection,
  nudges,
  treatmentPlansComponent: TreatmentPlans,
  clientInformationProgress: ClientInformationProgress,
}: TreatmentTabProps) {
  return (
    <>
      {shouldShowClinicalConsiderationSection && (
        <ClinicalConsiderationsSection
          nudges={nudges}
          patientId={patientId}
          isInSessionWithSelectedClient={isInSessionWithSelectedClient}
        />
      )}
      <div data-testid='client-info.treatment.progress'>
        <ClientInformationProgress />
      </div>
      {shouldShowFormsSection && (
        <Card>
          <ClientInformationFormsAndQuestionnaires completedForms={completedForms} hasNewForms={hasNewForms} />
        </Card>
      )}
      <div className='pt-4' data-testid='client-info.treatment.treatment-plans'>
        <TreatmentPlans
          patientShortId={patientShortId}
          onClick={() => onClickTreatmentPlans(TreatmentPlanStatus.CURRENT_GOALS)}
        />
      </div>
    </>
  );
}

export function TreatmentTabWrapper() {
  const selectedClientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClientInfo?.patientShortId ?? '';
  useGetFormsByPatientShortId(patientShortId);
  const completedForms = useAtomValue(completedFormsForSelectedClientAtom);
  const nudges = selectedClientInfo?.nudges ?? [];
  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));
  const patientId = useAtomValue(selectedPatientId) ?? '';

  const setDrawerState = useSetAtom(drawerStateAtom);
  const setTreatmentPlanContentStatus = useSetAtom(treatmentPlanContentStatus);
  const onClickTreatmentPlans = (treatmentPlanStatus: TreatmentPlanStatus) => {
    setTreatmentPlanContentStatus(treatmentPlanStatus);
    setDrawerState(ProviderDrawerState.TREATMENT_PLANS_CONTENT);
  };

  const hasCompletedForms = completedForms && Object.values(completedForms).length > 0;
  const shouldShowClinicalConsiderationSection = !!nudges.length;

  const isInSessionWithSelectedClient = useAtomValue(isInSessionWithSelectedClientAtom);
  const shouldShowFormsSection = isInSessionWithSelectedClient || hasCompletedForms;

  useSendClientInfoTabViewTrackingEvent(patientShortId, EventTypeEnum.TREATMENT_TAB);

  return (
    <TreatmentTab
      hasNewForms={hasNewForms}
      nudges={nudges}
      completedForms={completedForms}
      patientShortId={patientShortId}
      onClickTreatmentPlans={onClickTreatmentPlans}
      shouldShowFormsSection={shouldShowFormsSection}
      shouldShowClinicalConsiderationSection={shouldShowClinicalConsiderationSection}
      patientId={patientId}
      isInSessionWithSelectedClient={isInSessionWithSelectedClient}
      treatmentPlansComponent={TreatmentPlansOverviewConnected}
      clientInformationProgress={ClientInformationProgress}
    />
  );
}
